import type { DialogComponentProps } from '@brightdrop/bd-ui';
import type { ComponentType, ReactNode, SVGProps } from 'react';

import type {
  ConsentRequestDto,
  OrgEnrollmentResponseDto,
  ProviderAuthDto,
} from '~/api/generated-api';
import type { EnrollOptions } from '~/common/models/catalog.model';
import type { GmfProviderAccountStatus } from '~/common/models/gmf.model';
import type { OverviewDetailsProps } from '~/common/overviewDetails/types';

export const PARTNER_IDS = { COAST: 'coast', FLEETIO: 'fleetio' } as const;
export type PartnerId = (typeof PARTNER_IDS)[keyof typeof PARTNER_IDS];

export type LogoVariant = 'icon' | 'wordmark';
/** Every partner needs both: wordmark on the Shop card, icon-only on the Account hub. */
export type PartnerBrand = Record<
  LogoVariant,
  ComponentType<SVGProps<SVGSVGElement>>
> & {
  accountCard: {
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    tileAppearance: 'brand' | 'outlined';
    iconAppearance: 'inverse' | 'default';
  };
};

/** The four slices of enrollment the manage page addresses, as enrollment tiles and as tabs. */
export const ENROLLMENT_VIEWS = {
  ENROLLED: 'enrolled',
  ERRORS: 'errors',
  PENDING: 'pending',
  ELIGIBLE: 'eligible',
} as const;
export type EnrollmentView =
  (typeof ENROLLMENT_VIEWS)[keyof typeof ENROLLMENT_VIEWS];

/** The Shop card only ever shows this subset. Errors and pending belong to the manage page. */
export const COUNT_KEYS = [
  ENROLLMENT_VIEWS.ELIGIBLE,
  ENROLLMENT_VIEWS.ENROLLED,
] as const;
export type CountKey = (typeof COUNT_KEYS)[number];

/** Which counts the Shop card shows and what it calls them. */
export type PartnerCount = {
  key: CountKey;
  labelKey: string;
  /** Only the eligible count renders a tooltip. */
  tooltipKey?: string;
};

export type PartnerVisibilityContext = {
  /** GMF admin-linking status; Fleetio's card gates on it, Coast ignores it. */
  gmfLink?: { status?: GmfProviderAccountStatus };
};

export type ShopConfig = {
  counts: PartnerCount[];
  isVisible: (ctx: PartnerVisibilityContext) => boolean;
};

/** A position in the link journey, not a screen. Widened only in this file. */
export type WizardStepId = 'confirmAccount' | 'selectVehicles';

/** Everything the wizard accumulates. Each step owns its own keys and patches only those. */
export type LinkDraft = {
  apiKey?: string;
  hasAcceptedTerms?: boolean;
  hasAgreedToShareData?: boolean;
  /** Built by the step that owns the terms checkbox; forwarded verbatim to createOrgEnrollment. */
  consents?: ConsentRequestDto[];
  /** Blank fleetIds/vehicleIds means "enroll all" — matches the server's own default. */
  vehicleSelection?: { fleetIds: string[]; vehicleIds: string[] };
  /** Kept apart from vehicleSelection: an empty individual pick must not read as "all". */
  enrollOption?: EnrollOptions;
};

export type SubView = {
  titleKey: string;
  /** A detour owns its own primary button, so "Next" does not leak into an upload screen. */
  primaryLabelKey: string;
  component: ComponentType<WizardStepProps>;
};

export type WizardStepProps = {
  draft: LinkDraft;
  onChange: (patch: Partial<LinkDraft>) => void;
  onValidityChange: (isValid: boolean) => void;
  onOpenSubView: (view: SubView | null) => void;
  /** From the matching /providers entry. Undefined while that fetch is in flight. */
  termsRequired?: boolean;
  /** From the matching /providers entry; true forces enroll-all, hiding individual selection. */
  allVehiclesOnly?: boolean;
  /** Reused verbatim from BDDialog so the two signatures cannot drift. */
  setAlertBanners: NonNullable<DialogComponentProps['setAlertBanners']>;
  /** Rendered beside the dialog's action buttons while this view is shown. */
  onFooterChange?: (content: ReactNode) => void;
};

export type WizardStep = {
  id: WizardStepId;
  labelKey: string;
  component: ComponentType<WizardStepProps>;
};

export type WizardConfig = {
  titleKey: string;
  steps: WizardStep[];
};

/** One fixed row in a data-shared section. The shared column stays blank until values are wired. */
export type DataSharedRow = {
  key: string;
  labelKey: string;
};

/** Props for a manage-page section. The page already resolved this partner. */
export type ManageSectionProps = {
  partner: PartnerConfig;
  /** Partners the organization has an ACTIVE link with, as resolved by the page. */
  linkedPartnerIds?: PartnerId[];
  /** False while `linkedPartnerIds` may still be missing a partner, e.g. before the GMF lookup settles. */
  isLinkedPartnersReady?: boolean;
};

/** A manage-page section. `sectionId` is the list key; function names are shortened in production. */
export type ManageSection = ComponentType<ManageSectionProps> & {
  sectionId: string;
};

/** Account-detail rows for one partner. Runs during render, so it may resolve copy with translateText.
 * NOTE: Subject to change based on API response shape.
 */
export type AccountFields = (
  enrollment?: OrgEnrollmentResponseDto
) => OverviewDetailsProps[];

/** One manage-page tab. The page renders these in order and shows the selected tab's sections. */
export type ManageTab = {
  id: string;
  labelKey: string;
  sections: ManageSection[];
};

/**
 * Per-partner structure for the manage page. The page renders `tabs` and does not
 * add a tab or a section of its own. Sections read the field, tile, and row lists.
 */
export type ManageConfig = {
  /** Runs during render, so it may resolve copy with translateText. */
  accountFields: AccountFields;
  enrollmentTiles: EnrollmentView[];
  dataSharedRows: DataSharedRow[];
  /** Body of the unlink confirmation; each partner words its own consequences. */
  unlinkConfirmBodyKey: string;
  tabs: ManageTab[];
};

export type PartnerDefaults = {
  shop: Pick<ShopConfig, 'isVisible'>;
  manage: Omit<ManageConfig, 'unlinkConfirmBodyKey'>;
};

export type PartnerConfig = {
  id: PartnerId;
  providerCode: ProviderAuthDto['providerCode'];
  /** Rollout flag, independent of the business rule in `shop.isVisible`. */
  flag: string;
  nameKey: string;
  shortNameKey?: string;
  descriptionKey: string;
  shop: ShopConfig;
  manage: ManageConfig;
  /** Optional until each partner's step bodies ship; required once the wizard is wired in. */
  wizard?: WizardConfig;
};
